class gTagEvents {
    constructor() {
      this.delegateBody = new Delegate(document.body);

      this.bindEvents();
      this.initIntersectionObserver();
      this.observeDOMChanges();
      this.domChanged();
      this.interceptCartApi();
    }
  
    bindEvents() {
      this.delegateBody.on("click", '[data-action="ga-send-select"], [data-ga-action="ga-send-select"]', this.handleSelectItem.bind(this));
      this.delegateBody.on("gtm:video:event", this.sendVideoJsEvent.bind(this));
    }

    initIntersectionObserver() {
      const settings =  {
        root: null, // Use the viewport as the root
        rootMargin: "0px",
        threshold: 0.1, // Adjust the threshold as per your requirement
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this), settings);
    }
  
    domChanged() {
      const allIntersections = document.body.querySelectorAll('[data-gtag-intersection][data-observed="false"]');

      allIntersections.forEach((item) => {
        this.intersectionObserver.observe(item);
        item.dataset.observed = "true";
      });
    }
  
    observeDOMChanges() {
      const domMutationObserver = new MutationObserver((mutationsList, observer) => {
        // For each mutation record, we look for added nodes
        for (const mutation of mutationsList) {
          if (mutation.addedNodes.length) {
            // If there are added nodes, we call domChanged to check for new items
            this.domChanged();
          }
        }
      });
  
      // Start observing the document body for added nodes and subtree modifications
      domMutationObserver.observe(document.body, {
        childList: true, // Observe direct children
        subtree: true, // and all descendants
        attributes: false // Set to true if you also want to observe attribute changes
      });
    }
  
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          let data = false;
            
          try {
            const json = entry.target.querySelector('script[type="application/ld+json"]').textContent;
            data = JSON.parse(json);
          } catch (e) {
            console.log("JSON parse failed on: ", entry, "Error: ", e);
          }
  
          if (!dataLayer || !data) return;

          dataLayer.push({ ecommerce: null });
          dataLayer.push(data);
          console.log("View item list pushed.", data);
        }
      });
    }
  
    handleSelectItem(event) {
      let target = event.target.closest('[data-action="ga-send-select"]') || event.target.closest('[data-ga-action="ga-send-select"]');
      if (!target || !dataLayer) return;

      const settings = JSON.parse(target.dataset.selectSettings);
      if (!settings) return;

      const customEvent = { 
          event: "custom_select_item",
          ecommerce: {
              item_list_id: settings.list_id,
              item_list_name: settings.list_name,
              currency: ShopifyAnalytics.meta.currency,
              items: [settings]
          }
      }
  
      dataLayer.push({ ecommerce: null });
      dataLayer.push(customEvent);
      console.log("dataLayer push -> custom_select_item:", customEvent);
  }

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        window.fetch = async function (...args) {
            try {
                const [url, request] = [...args];

                // Trigger custom logic for cart clear API
                if (url.includes(`/cart/clear`)) _this.cartClear();
                if (url.includes(`/cart/update`)) _this.cartUpdate(request.body);

                const response = await originalFetch.apply(this, args);
                if (!response.ok) return response;
                
                 // Early return for non-cart-related APIs or missing conditions
                 if (url === '' || !dataLayer) return response;             

                // Process cart actions based on URL
                if (url.includes(`/cart/add`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json(); 

                    _this.cartAdd(json, request);
                } else if (url.includes(`/cart/change`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json(); 

                    _this.cartChange(json);
                }

                return response;

            } catch (e) {
                console.error('Fetch interceptor error:', e);
                return originalFetch(...args);
            }
        };
    }

    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        cartItems = this.getDataFromRequest(cartItems, request);
        this.productAdded(cartItems);
    }

    async cartUpdate(body) {
      if (!body) return;
      const updates = JSON.parse(body).updates;
  
      const oldCart = await this.getCart();
      const cartItems = oldCart.items;
      let itemsAdded = [], itemsRemoved = [];

      cartItems.forEach(item => {            
          const newQuantity = updates[item.key];
          if (typeof newQuantity === "number") {
              const change = newQuantity - item.quantity;
              item.quantity = Math.abs(change);
  
              change < 1 ? itemsRemoved.push(item) : itemsAdded.push(item);
          }
      })

      itemsAdded = this.setLocationShoppingCart(itemsAdded);
      itemsRemoved = this.setLocationShoppingCart(itemsRemoved);
  
      this.productAdded(itemsAdded);
      this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart) {      
      const itemsAdded = this.setLocationShoppingCart(cart.items_added);
      const itemsRemoved = this.setLocationShoppingCart(cart.items_removed);

      this.productAdded(itemsAdded);
      this.productsRemoved(itemsRemoved);
    }

    productAdded(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': 'custom_add_to_cart',
            'ecommerce': this.buildEcommerce(cartItems)
        }

        dataLayer.push({ ecommerce: null });
        dataLayer.push(customEvent);
        console.log("dataLayer push -> add_to_cart_custom:", customEvent);
    }

    productsRemoved(cartItems) {
      if (!cartItems || !cartItems.length) return;

      const customEvent = {
            'event': "custom_remove_from_cart",
            'ecommerce': this.buildEcommerce(cartItems)
      }

      dataLayer.push({ ecommerce: null });
      dataLayer.push(customEvent);
      console.log("dataLayer push -> custom_remove_from_cart:", customEvent);
    }

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;        
        const global_item_id = cartItems[0].list_id;
        const global_item_name = cartItems[0].list_name;
        cartItems.forEach((item) => {            

            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            totalPrice += price * item.quantity;
            
            const product = {
                'item_id': item.sku || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price, 
                'quantity': item.quantity,
                'category': item["category"] || "",
                'category2': item["category2"] || "",
                'item_brand': item["item_brand"] || "",
                'item_list_id': item["list_id"] || "",
                'item_list_name': item["list_name"] || ""
            }

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': ShopifyAnalytics.meta.currency,
            'item_list_id':global_item_id,
            'item_list_name': global_item_name,
            'items': items
        }
    }

    getDataFromRequest(cartItems, request) {
        const body = request?.body || null;
        if (!body) return cartItems;

        const json = JSON.parse(body);

        const quantity = json["quantity"] || 1;
        cartItems[0].quantity = Number(quantity);
        
        cartItems[0]["category"] = json["_gtm-category"] || "";
        cartItems[0]["category2"] = json["_gtm-category2"] || "";
        cartItems[0]["item_brand"] = json["_gtm-item_brand"] || "";
        cartItems[0]["list_id"] = json["_gtm-list_id"] || "";
        cartItems[0]["list_name"] = json["_gtm-list_name"] || "";

        return cartItems;
    }

    setLocationShoppingCart(array) {
      return array.map(item => {            
          item["list_id"] = "shopping_cart"
          item["list_name"] = "Shopping cart"

          
          const lineItem = document.querySelector(`[data-gtm-id="${item.view_key}"]`);          
          if (!lineItem) return item;

          const category = lineItem.dataset.gtmCategory;
          const category2 = lineItem.dataset.gtmCategory2;
          const brand = lineItem.dataset.gtmItemBrand;

          console.log(category, category2);

          if (category) item["category"] = category;
          if (category2) item["category2"] = category2;
          if (brand) item["item_brand"] = brand;

          return item;
      });
    }

    sendVideoJsEvent() {
      const state = event.detail?.state;
      if (!state) return;

      const eventName = state === "playing" ? "custom_video_start" : "custom_video_stop";

      dataLayer.push({ ecommerce: null });
      dataLayer.push({ 'event': eventName });
      console.log("dataLayer push ->", eventName);
    }
  }
  
  // function getTrackingConsentCookie() {
  //   // Split the document.cookie string into an array of "key=value" strings
  //   var cookiesArray = document.cookie.split(";");
  
  //   // Loop through the array of cookies
  //   for (var i = 0; i < cookiesArray.length; i++) {
  //     var cookie = cookiesArray[i].trim();
  
  //     // Check if the cookie's name is "_tracking_consent"
  //     if (cookie.startsWith("_tracking_consent=")) {
  //       // Return the value part of "key=value"
  //       return cookie.substring("_tracking_consent=".length, cookie.length);
  //     }
  //   }
  
  //   // Return null if the "_tracking_consent" cookie is not found
  //   return null;
  // }
  
  // let consent = false;
  
  // (function checkForConsent() {
  //   // Logs the value of the _tracking_consent cookie or null if not found
  //   const trackingConsent = getTrackingConsentCookie();
  //   if (!trackingConsent) return;
  
  //   const decodedString = decodeURIComponent(trackingConsent);
  
  //   // Parse the JSON string to an object
  //   const jsonObject = JSON.parse(decodedString);
  //   if (jsonObject.con.CMP.a == 1) {
  //     consent = true;
  //   }
  // })();
  
  // function addEventListenerForAcceptBtn(adittionalChecks = 0) {
  //   if (window.isenseGDPR !== undefined) {
  //     window.addEventListener("click", function (e) {
  //       const isAcceptButton =
  //         e.target &&
  //         typeof e.target.className === "string" &&
  //         e.target.className.indexOf("isense-cc-submit-consent") >= 0 &&
  //         (e.target.className.indexOf("cc-btn-accept-all") >= 0 ||
  //           e.target.className.indexOf("cc-allow") >= 0);
  
  //       if (isAcceptButton) {
  //         new gTagEvents();
  //       }
  //     });
  //   } else {
  //     if (adittionalChecks > 0) {
  //       setTimeout(function () {
  //         addEventListenerForAcceptBtn(adittionalChecks - 1);
  //       }, 500);
  //     }
  //   }
  // }
  
  /*
  if (window.theme.store === "regular") {
    if (consent == true) {
      document.addEventListener("DOMContentLoaded", (event) => {
        new gTagEvents();
      });
    } else {
      addEventListenerForAcceptBtn(40);
    }
  } else {
    if (consent != true) {
      window.addEventListener('PandectesEvent_OnConsent', function (event) {
        if (event.detail.consentType === 'new' || event.detail.consentType == 'stored') {
          if ((event.detail.preferences & 4) === 0) {
            new gTagEvents();
          }
        }
      });
    } else {
      new gTagEvents();
    }
  }*/

new gTagEvents();